import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Phone, ChevronDown } from "lucide-react";
import { PHONE, TEL } from "@/lib/config";
import { InstallButton } from "./PwaBits";
import { useContent } from "@/lib/ContentContext";
import { resolveImage } from "@/lib/api";
import SharePageButton from "./SharePage";

const DEFAULT_LOGO = "/brand/logo.png";

const MENU = [
  { label: "Home", to: "/" },
  { label: "Services", to: "/services", children: [
    { label: "Commercial Soft-Wash", to: "/services/commercial-soft-wash" },
    { label: "Paver Sealing", to: "/services/paver-sealing" },
    { label: "High-Pressure Washing", to: "/services/high-pressure-washing" },
    { label: "Driveway & Concrete Cleaning", to: "/services/driveway-concrete-cleaning" },
    { label: "Pool Area Cleaning", to: "/services/pool-area-cleaning" },
  ]},
  { label: "Residential", to: "/residential" },
  { label: "Commercial", to: "/commercial" },
  { label: "Our Work", to: "/before-after", children: [
    { label: "Before & After", to: "/before-after" },
    { label: "Reviews", to: "/reviews" },
  ]},
  { label: "About", to: "/about", children: [
    { label: "About Us", to: "/about" },
    { label: "Service Areas", to: "/service-areas" },
    { label: "FAQ", to: "/faq" },
    { label: "Financing", to: "/financing" },
  ]},
  { label: "Contact", to: "/contact" },
];

export const Header = () => {
  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const [scrolled, setScrolled] = useState(false);
  const loc = useLocation();
  const { content } = useContent();
  const logoSrc = content?.site_logo ? resolveImage(content.site_logo) : DEFAULT_LOGO;

  useEffect(() => { setOpen(false); setExpanded(null); }, [loc.pathname]);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header className={`sticky top-0 z-40 glass-header border-b border-gray-200/60 ${scrolled ? "card-shadow" : ""}`} data-testid="site-header">
      <div className="max-w-7xl mx-auto px-4 flex items-center justify-between h-[74px] lg:h-[80px]">
        {/* Logo + name */}
        <Link to="/" className="flex items-center gap-3 shrink-0" data-testid="header-logo">
          <img src={logoSrc} alt="Veterans Power Wash & Cleaning Services logo" className="h-12 lg:h-14 w-auto max-w-[180px] rounded-lg object-contain" />
          <span className="leading-[1.05]">
            <span className="block font-head font-bold text-[var(--navy)] text-base lg:text-xl uppercase tracking-tight">Veterans Power Wash</span>
            <span className="block font-head font-semibold text-[var(--text-muted)] text-[11px] lg:text-sm uppercase tracking-wide">& Cleaning Services</span>
          </span>
        </Link>

        {/* Desktop nav */}
        <nav className="hidden lg:flex items-center gap-1.5 ml-auto mr-4" data-testid="desktop-nav">
          {MENU.map((n) => (
            <div key={n.label} className="relative group">
              <Link to={n.to}
                className={`flex items-center gap-1 px-3.5 py-2 text-[15px] font-medium rounded-md whitespace-nowrap transition-colors hover:text-[var(--action)] ${loc.pathname === n.to ? "text-[var(--action)]" : "text-[var(--navy)]"}`}>
                {n.label}{n.children && <ChevronDown className="w-3.5 h-3.5 opacity-60 group-hover:rotate-180 transition-transform" />}
              </Link>
              {n.children && (
                <div className="absolute left-0 top-full pt-2 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-150 z-50">
                  <div className="bg-white rounded-xl card-shadow-lg border border-gray-100 py-2 min-w-[240px]">
                    {n.children.map((c) => (
                      <Link key={c.to} to={c.to} className="block px-4 py-2.5 text-sm text-[var(--navy)] hover:bg-[var(--surface)] hover:text-[var(--action)]">{c.label}</Link>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-2 shrink-0">
          <SharePageButton />
          <Link to="/get-a-quote" data-testid="header-quote-btn"
            className="btn-lift bg-[var(--red)] hover:bg-[var(--red-dark)] text-white font-semibold text-sm px-6 py-2.5 rounded-full">
            Get a Free Quote
          </Link>
        </div>

        {/* Mobile controls */}
        <div className="flex items-center gap-1 lg:hidden">
          <SharePageButton />
          <a href={TEL} className="p-2.5 text-[var(--red)]" aria-label="Call"><Phone className="w-5 h-5" /></a>
          <button className="p-2.5 text-[var(--navy)]" onClick={() => setOpen(!open)} data-testid="mobile-menu-toggle" aria-label="Menu">
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {open && (
        <div className="lg:hidden bg-white border-t border-gray-200 max-h-[80vh] overflow-y-auto" data-testid="mobile-nav">
          <div className="px-4 py-3 flex flex-col">
            {MENU.map((n) => (
              <div key={n.label} className="border-b border-gray-100">
                {n.children ? (
                  <>
                    <button onClick={() => setExpanded(expanded === n.label ? null : n.label)}
                      className="w-full flex items-center justify-between py-3 font-medium text-[var(--navy)]">
                      {n.label}<ChevronDown className={`w-4 h-4 transition-transform ${expanded === n.label ? "rotate-180" : ""}`} />
                    </button>
                    {expanded === n.label && (
                      <div className="pb-2 pl-3 flex flex-col">
                        <Link to={n.to} className="py-2 text-sm text-[var(--action)] font-medium">All {n.label}</Link>
                        {n.children.map((c) => <Link key={c.to} to={c.to} className="py-2 text-sm text-[var(--text-muted)]">{c.label}</Link>)}
                      </div>
                    )}
                  </>
                ) : (
                  <Link to={n.to} className="block py-3 font-medium text-[var(--navy)]">{n.label}</Link>
                )}
              </div>
            ))}
            <Link to="/get-a-quote" className="mt-4 text-center bg-[var(--red)] text-white font-semibold py-3 rounded-full">Get a Free Quote</Link>
            <div className="mt-3 flex justify-center"><SharePageButton variant="full" /></div>
            <div className="mt-3 flex justify-center"><InstallButton /></div>
          </div>
        </div>
      )}
    </header>
  );
};
